<template>
  <PopOverMenu
    class="dataset-label-edit-menu"
    :class="{ 'dataset-label-edit-menu--with-delete': canDelete }"
    placement="bottom-end"
    @state:change="handleMenuStateChange"
  >
    <template #target>
      <ThreeDotButton
        flair="soft"
        kind="transparent"
        size="sm"
        class="dataset-label-edit-menu__trigger"
        aria-label="Edit label menu"
      />
    </template>
    <template #menu="{ closeMenu }">
      <Transition
        :name="isCustomColorPickerOpen ? 'slide-right' : 'slide-left'"
        @after-enter="focusInputField"
      >
        <CustomColorPicker
          v-if="isCustomColorPickerOpen"
          ref="inputFieldRef"
          key="color-picker"
          hint-text="Choose a HEX code to customize your label color."
          :color="isCustomColor ? datasetLabel.color : ''"
          @save="handleCustomColorSubmit($event, closeMenu)"
          @close="isCustomColorPickerOpen = false"
        />
        <CardSection
          v-else
          key="default-view"
          class="dataset-label-edit-menu__content"
        >
          <div class="dataset-label-edit-menu__header">
            <InputField
              ref="inputFieldRef"
              :model-value="datasetLabel.name"
              :max-length="50"
              placeholder="Dataset label name"
              required
              @update:model-value="debouncedUpdateName"
            />
          </div>
          <div class="dataset-label-edit-menu__title">Colors</div>
          <div class="dataset-label-edit-menu__list">
            <ListItemWithState
              v-for="presetColor in enhancedPresetColors"
              :key="presetColor.name"
              :selected="presetColor.selected"
              class="dataset-label-edit-menu__item"
              size="sm"
              @click="handleUpdateColorAndClose(presetColor.bgColor, closeMenu)"
            >
              <template #prepend>
                <ColorTile
                  class="dataset-label-edit-menu__item-color-tile"
                  :color="presetColor.bgColor"
                  :border-color="presetColor.textColor"
                  size="large"
                />
              </template>
              <template #default>{{ presetColor.name }}</template>
            </ListItemWithState>
            <ListItemWithState
              class="dataset-label-edit-menu__item"
              :selected="isCustomColor"
              size="sm"
              @click="isCustomColorPickerOpen = true"
            >
              <template #prepend>
                <ColorTile
                  class="dataset-label-edit-menu__item-color-tile"
                  :color="isCustomColor ? datasetLabel.color : ''"
                  size="large"
                />
              </template>
              <template #default>Custom color</template>
            </ListItemWithState>
          </div>
          <div
            v-if="canDelete"
            class="dataset-label-edit-menu__footer"
          >
            <DarwinButton
              size="sm"
              flair="rounded"
              kind="secondary"
              @click="deleteLabel"
            >
              <template #icon-prepend>
                <Icon name="mono-trash" />
              </template>
              Delete Label
            </DarwinButton>
          </div>
        </CardSection>
      </Transition>
    </template>
  </PopOverMenu>
</template>

<script setup lang="ts">
import { computed, ref, nextTick } from 'vue'
import debounce from 'lodash/debounce'

import ThreeDotButton from '@/uiKit/Icons/ThreeDotButton.vue'
import CardSection from '@/uiKit/Card/CardSection.vue'
import PopOverMenu from '@/uiKit/PopOverMenu.vue'
import ListItemWithState from '@/uiKit/List/ListItemWithState.vue'
import InputField from '@/components/Common/InputField/V2/InputField.vue'
import DarwinButton from '@/uiKit/Button/DarwinButton.vue'
import Icon from '@/uiKit/Icon/Icon.vue'
import CustomColorPicker from '@/uiKit/ColorPicker/CustomColorPicker.vue'
import ColorTile from '@/uiKit/ColorPicker/ColorTile.vue'

import { datasetLabelPresetColors } from '@/modules/Datasets/datasetLabelTypes'
import type { DatasetLabelPayload } from '@/store/types/DatasetLabelPayload'

const emit = defineEmits<{
  (e: 'update:dataset-label', v: { name: string; color: string }): void
  (e: 'delete:dataset-label'): void
  (e: 'state:change', v: boolean): void
}>()

const props = defineProps<{
  datasetLabel: DatasetLabelPayload
  canDelete?: boolean
}>()

const inputFieldRef = ref<InstanceType<typeof InputField>>()
const isCustomColorPickerOpen = ref(false)

const isCustomColor = computed(() =>
  datasetLabelPresetColors.every((c) => c.bgColor !== props.datasetLabel.color),
)

const enhancedPresetColors = computed(() => {
  const currentColor = props.datasetLabel.color
  return datasetLabelPresetColors.map((c) => ({
    ...c,
    selected: c.bgColor === currentColor,
  }))
})

const updateColor = (color: string): void => {
  emit('update:dataset-label', {
    name: props.datasetLabel.name,
    color,
  })
  emit('state:change', false)
}

const updateName = (name: string): void => {
  if (!name.trim().length) return

  emit('update:dataset-label', {
    name,
    color: props.datasetLabel.color,
  })
}

const debouncedUpdateName = debounce(updateName, 500)

const deleteLabel = (): void => {
  emit('delete:dataset-label')
}

const handleCustomColorSubmit = (color: string, closeMenu: () => void): void => {
  isCustomColorPickerOpen.value = false
  updateColor(color)
  closeMenu()
}

const handleUpdateColorAndClose = (color: string, closeMenu: () => void): void => {
  updateColor(color)
  closeMenu()
}

// We cannot use the "autofocus" prop on InputField — it immediately attempts to scroll and focus the input,
// which interferes with Vue's transition animations and can cause layout glitches.
const focusInputField = (): void => {
  nextTick(() => {
    inputFieldRef.value?.setFocus()
  })
}

const handleMenuStateChange = (isOpen: boolean): void => {
  if (isOpen) {
    focusInputField()
  } else {
    isCustomColorPickerOpen.value = false
  }

  emit('state:change', isOpen)
}
</script>

<style lang="scss" scoped>
@import '@/uiKit/assets/spacings.scss';

.dataset-label-edit-menu {
  &__trigger:hover {
    background-color: var(--color-background-gray-subtlest-hovered);
  }

  & &__content {
    position: absolute;
    top: 0;
    left: 0;
    padding: 0;
    width: 100%;
    height: 100%;
    background-color: var(--color-surface-default);
  }

  &__header {
    padding: 5px;
    border-bottom: 1px solid var(--color-border-default);
  }

  &__title {
    @include typographyRegularBody200;
    padding: $spacing-2 $s-lg $spacing-1;
    color: var(--color-text-subtle);
    text-align: left;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: $spacing-1;
    padding: $spacing-1 $spacing-2;
  }

  &__item {
    padding: $spacing-2;

    & &-color-tile {
      border-radius: $spacing-3;
    }
  }

  &__footer {
    display: flex;
    gap: $spacing-1;
    padding: $spacing-2;
    border-top: 1px solid var(--color-border-default);

    :deep(.darwin-button) {
      width: 100%;
    }
  }

  :deep(.popover-menu__content) {
    position: relative;
    width: 182px;
    min-height: 316px;
    overflow-x: hidden !important;
  }

  &--with-delete :deep(.popover-menu__content) {
    min-height: 356px;
  }
}
</style>
